import {call, put, takeLatest, take, fork, select, delay, cancel, all, race} from 'redux-saga/effects'
import {doc, getDoc, setDoc, collection, query, where, getDocs, onSnapshot, updateDoc} from 'firebase/firestore'
import {eventChannel} from 'redux-saga'
import type {PayloadAction} from '@reduxjs/toolkit'
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
} from 'firebase/auth'
import type { User } from 'firebase/auth'
import { showToast } from '@/components/app/Toast/showToast'
import {auth, db, functions} from '../../lib/firebase'
import {httpsCallable} from 'firebase/functions'
import {
    initAuthSaga,
    signInSaga,
    signInGoogleSaga,
    resetPasswordSaga,
    logoutSaga,
    verifyEmailSaga,
    resendVerificationSaga,
    updateFullNameSaga,
    setFullName,
    updateUserAvatarSaga,
    setAvatarUrl,
    updateUsernameSaga,
    setUsername,
    updateEmailSaga,
    setEmail,
    sendEmailChangeSaga,
    verifyEmailChangeSaga,
    initiateDeleteAccountSaga,
    deleteAccountSaga,
    setAuth,
    resetAuth,
    setWorkspaces,
    setWorkspacesLoading,
    updateLoginSectionSaga,
    setLoginSection,
    updateLastWorkspaceSaga,
    setLastWorkspaceUrlId,
    loadNotificationSettingsSaga,
    updateNotificationSettingsSaga,
    setNotificationSettings,
    DEFAULT_NOTIFICATION_SETTINGS,
} from './authSlice'
import type {UserWorkspace, LoginSection, NotificationSettings, AuthState} from './authSlice'
import {signInWithGoogle, handleGoogleRedirect, hasPendingGoogleRedirect} from '../../lib/auth'
import {setOpenModal, setGlobalLoading, setSignInError, setResetPasswordError, openConfirmDialog, addLoadingKey, removeLoadingKey} from '../ui/uiSlice'
import {store} from '../index'
import {resetState} from '../actions'
import {resolveWorkspaceEntry, sanitizeNext} from './workspaceEntry'
import {createInitialState} from '../initialState/initialStateSaga'
import {history} from '../../lib/history'
import type {RootState} from '../index'
import {logTiming} from '@/utils/timing'
import {resolveDefaultWorkspace} from '@/utils/defaultWorkspace'
import {cacheWorkspaceUrlIds} from '../workspace/workspaceUrlCache'
import {setLoading as setProjectsLoading} from '../projects/projectsSlice'
import {AnalyticsEvent, identifyUser, resetUser, trackEvent, setPersonProps, markJustSignedUp, consumeJustSignedUp} from '@/lib/analytics'

function createAuthChannel() {
    return eventChannel((emit) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            emit(user ?? 'LOGGED_OUT')
        })
        return unsubscribe
    })
}

function createMembersChannel(userId: string) {
    return eventChannel(emit => {
        const membersQuery = query(
            collection(db, 'workspaceMembers'),
            where('userId', '==', userId),
            where('status', '==', 'active')
        )
        const unsub = onSnapshot(
            membersQuery,
            (snapshot) => emit({ type: 'data', snapshot }),
            (error) => emit({ type: 'error', error })
        )
        return unsub
    })
}

function* listenUserWorkspacesWorker(userId: string): Generator<any, void, any> {
    if (!userId) {
        yield put(setWorkspaces([]))
        yield put(setWorkspacesLoading(false))
        return
    }

    // Only show the loading gate if workspaces aren't already populated (loadUserData
    // pre-populates them in setAuth). Otherwise this listener is just live updates and
    // must not re-gate app load behind its redundant workspace-doc re-fetch.
    const existingWorkspaces: UserWorkspace[] = yield select((s: RootState) => s.auth?.workspaces ?? [])
    if (existingWorkspaces.length === 0) {
        yield put(setWorkspacesLoading(true))
    }

    logTiming(`[userWorkspaces] listener started (first onSnapshot in session)`)
    const channel: ReturnType<typeof createMembersChannel> = yield call(createMembersChannel, userId)

    let firstData = true
    try {
        while (true) {
            const event: any = yield take(channel)

            if (event.type === 'data') {
                if (firstData) {
                    logTiming(`[userWorkspaces] first snapshot (${event.snapshot.size} docs) — WebChannel open`)
                    firstData = false
                }
                try {
                    const memberDocs = event.snapshot.docs.map((d: any) => ({
                        workspaceId: d.data().workspaceId as string,
                        role: d.data().role as 'owner' | 'member' | 'guest'
                    }))

                    // Skip getDoc calls when loadUserData already cached all workspace docs.
                    // Saves N reads on every login; only falls through when membership changes.
                    const cached: UserWorkspace[] = yield select((s: RootState) => s.auth?.workspaces ?? [])
                    const cachedById = new Map(cached.map(w => [w.id, w]))
                    if (cachedById.size > 0 && memberDocs.every((m: { workspaceId: string }) => cachedById.has(m.workspaceId))) {
                        yield put(setWorkspaces(memberDocs.map((m: { workspaceId: string; role: 'owner' | 'member' | 'guest' }) => ({
                            ...cachedById.get(m.workspaceId)!,
                            role: m.role
                        }))))
                        yield put(setWorkspacesLoading(false))
                        continue
                    }

                    const workspaces: UserWorkspace[] = yield call(async () => {
                        const workspacePromises = memberDocs.map(async (member: { workspaceId: string; role: string }) => {
                            const workspaceDoc = await getDoc(doc(db, 'workspaces', member.workspaceId))
                            if (workspaceDoc.exists()) {
                                const data = workspaceDoc.data()
                                return {
                                    id: workspaceDoc.id,
                                    name: data.name,
                                    workspaceUrlId: data.workspaceUrlId,
                                    workspaceTaskPrefixId: data.workspaceTaskPrefixId,
                                    avatar: data.avatar,
                                    role: member.role
                                } as UserWorkspace
                            }
                            return null
                        })
                        const results = await Promise.all(workspacePromises)
                        return results.filter((w: UserWorkspace | null): w is UserWorkspace => w !== null)
                    })

                    yield put(setWorkspaces(workspaces))
                    yield put(setWorkspacesLoading(false))
                } catch (err) {
                    console.error('Error loading workspaces:', err)
                    yield put(setWorkspacesLoading(false))
                }
            } else {
                console.error('Error listening to workspaceMembers:', event.error)
                yield put(setWorkspacesLoading(false))
            }
        }
    } finally {
        channel.close()
    }
}

type LoadUserResult = 'no-user' | 'no-workspaces' | 'redirecting' | 'ready' | 'needs-verification' | 'select-workspace' | 'standalone'

function* loadUserData(user: User): Generator<any, LoadUserResult, any> {
    logTiming(`[auth] loadUserData start, uid=${user.uid}`)

    const userRef = doc(db, 'users', user.uid)
    // The user doc and the user's memberships are independent — fetch them in parallel.
    // Query only by userId (uses automatic single-field index), filter status client-side.
    const workspaceMembersQuery = query(
        collection(db, 'workspaceMembers'),
        where('userId', '==', user.uid)
    )
    logTiming(`[auth] → getDoc users + getDocs workspaceMembers (parallel)`)
    // A brand-new user's users/{uid} doc is written by handleGoogleRedirect or the
    // onUserCreated trigger. On a first Google sign-in where the pendingGoogleRedirect
    // flag was lost (Safari ITP, rare), neither may have run yet when we get here — so
    // don't bail straight to resetAuth on the first miss (that kicks the user out on
    // their very first login). Retry briefly to let the doc land. (SCD-531)
    let userDoc: any
    let allMembersSnapshot: any
    for (let attempt = 1; ; attempt++) {
        try {
            [userDoc, allMembersSnapshot] = yield all([
                call(getDoc, userRef),
                call(getDocs, workspaceMembersQuery),
            ])
        } catch (err) {
            const code = (err as { code?: string })?.code
            // permission-denied on the first authed read after an in-session signup:
            // the Firestore long-poll connection was opened while logged out (guest)
            // and stays unauthenticated, so reads are denied persistently until the
            // page reloads (the Google redirect path already reloads, which is why it
            // works). Reload once to re-init Firestore with auth present. A
            // sessionStorage guard prevents a reload loop if it's a genuine denial.
            if (code === 'permission-denied') {
                if (!sessionStorage.getItem(AUTH_RELOAD_FLAG)) {
                    sessionStorage.setItem(AUTH_RELOAD_FLAG, '1')
                    logTiming('[auth] permission-denied on first read → reloading once to re-auth Firestore')
                    window.location.reload()
                    yield delay(10000) // hold until the reload takes over
                    return 'redirecting'
                }
                logTiming('[auth] permission-denied persists after reload → giving up')
                throw err
            }
            // Other transient read failures: retry instead of crashing initAuthSaga
            // (an uncaught error left globalLoading=true forever → eternal loader).
            logTiming(`[auth] loadUserData read failed (attempt ${attempt}/${USER_DOC_MAX_ATTEMPTS}): ${(err as Error)?.message ?? err}`)
            if (attempt >= USER_DOC_MAX_ATTEMPTS) throw err
            yield delay(USER_DOC_RETRY_MS)
            continue
        }
        if (userDoc.exists() || attempt >= USER_DOC_MAX_ATTEMPTS) break
        logTiming(`[auth] users/${user.uid} missing, retry ${attempt}/${USER_DOC_MAX_ATTEMPTS}`)
        yield delay(USER_DOC_RETRY_MS)
    }
    logTiming(`[auth] ← getDoc + getDocs done (exists=${userDoc.exists()})`)
    // Read succeeded — clear the recovery-reload guard so a future stuck state can reload again.
    try { sessionStorage.removeItem(AUTH_RELOAD_FLAG) } catch { /* ignore */ }

    if (userDoc.exists()) {
        const data = userDoc.data() as {fullName?: string, color?: string, avatarImage?: string, username?: string, emailVerified?: boolean, timezone?: string, loginSection?: LoginSection, lastWorkspaceUrlId?: string | null}

        if (data.emailVerified === false) {
            yield put(setAuth({
                id: user.uid,
                email: user.email || '',
                fullName: data.fullName || '',
                color: data.color || '',
                avatarImage: data.avatarImage || '',
                username: data.username || '',
                workspaces: [],
                timezone: data.timezone || '',
                loginSection: data.loginSection || 'projects',
                lastWorkspaceUrlId: data.lastWorkspaceUrlId ?? null,
                workspacesLoading: false,
                notificationSettings: DEFAULT_NOTIFICATION_SETTINGS,
            }))
            yield put(setOpenModal({ type: 'verifyEmail', email: user.email || '' }))
            logTiming('[auth] loadUserData → needs-verification')
            return 'needs-verification'
        }

        // Memberships were fetched in parallel above (needed to resolve the redirect URL).
        const activeDocs = allMembersSnapshot.docs.filter((d: any) => d.data().status === 'active')
        logTiming(`[auth] members: ${allMembersSnapshot.size} total, ${activeDocs.length} active`)

        if (activeDocs.length === 0) {
            // The one-shot membership read came back empty. Do NOT imperatively open the
            // onboarding modal here — on a deep-link / cold mobile load this read can resolve
            // from an empty cache before the live listener delivers the real memberships, and
            // an imperatively-opened canClose:false modal would then sit forever over a
            // workspace that loads a moment later (SCD-933). Instead keep workspacesLoading=true
            // and let the live listener (listenUserWorkspacesWorker, forked below) settle it:
            // the reactive onboarding gate (selectShouldShowCreateWorkspaceOnboarding) shows the
            // modal only once it confirms genuinely zero workspaces, and hides it the moment a
            // real workspace arrives.
            yield put(setAuth({
                id: user.uid,
                email: user.email || '',
                fullName: data.fullName || '',
                color: data.color || '',
                avatarImage: data.avatarImage || '',
                username: data.username || '',
                timezone: data.timezone || '',
                loginSection: data.loginSection || 'projects',
                lastWorkspaceUrlId: data.lastWorkspaceUrlId ?? null,
                workspaces: [],
                workspacesLoading: true,
                notificationSettings: DEFAULT_NOTIFICATION_SETTINGS,
            }))
            logTiming('[auth] loadUserData → no-workspaces')
            return 'no-workspaces'
        } else {
            const workspaceIds = activeDocs.map((d: any) => {
                const data = d.data() as { workspaceId: string }
                return data.workspaceId
            })

            logTiming(`[auth] → getDoc workspaces parallel (${workspaceIds.length})`)
            const workspaceDocs: any[] = yield all(
                workspaceIds.map((id: string) => call(() => getDoc(doc(db, 'workspaces', id))))
            )
            logTiming(`[auth] ← getDoc workspaces parallel done`)

            const roleByWorkspaceId = new Map<string, 'owner' | 'member' | 'guest'>()
            for (const m of activeDocs) {
                const md = m.data() as { workspaceId: string; role: 'owner' | 'member' | 'guest' }
                roleByWorkspaceId.set(md.workspaceId, md.role)
            }

            const workspaceUrlIds: string[] = []
            const urlIdEntries: Array<{ urlId: string; workspaceId: string }> = []
            // Build the user's workspaces (with role) here so auth.workspaces is populated in
            // setAuth — projectsSaga reads role at fork time to pick the owner vs member query,
            // and we now fork it before the userWorkspaces listener delivers.
            const userWorkspaces: UserWorkspace[] = []
            for (const workspaceDocSnap of workspaceDocs) {
                if (workspaceDocSnap.exists()) {
                    const workspaceData = workspaceDocSnap.data() as { workspaceUrlId?: string; name?: string; workspaceTaskPrefixId?: string; avatar?: string | null }
                    if (workspaceData.workspaceUrlId) {
                        workspaceUrlIds.push(workspaceData.workspaceUrlId)
                        urlIdEntries.push({ urlId: workspaceData.workspaceUrlId, workspaceId: workspaceDocSnap.id })
                    }
                    userWorkspaces.push({
                        id: workspaceDocSnap.id,
                        name: workspaceData.name ?? '',
                        workspaceUrlId: workspaceData.workspaceUrlId ?? '',
                        workspaceTaskPrefixId: workspaceData.workspaceTaskPrefixId ?? '',
                        avatar: workspaceData.avatar ?? null,
                        role: roleByWorkspaceId.get(workspaceDocSnap.id) ?? 'member',
                    })
                }
            }
            // Cache so workspaceSaga can skip the workspaceUrlIds → workspaceId lookup.
            cacheWorkspaceUrlIds(urlIdEntries)

            const currentPath = window.location.pathname
            const pathParts = currentPath.split('/').filter(Boolean)
            const currentWorkspaceUrlId = pathParts[0] || ''

            let showWorkspacePicker = false
            let renderStandalone = false
            if (workspaceUrlIds.length > 0 && !workspaceUrlIds.includes(currentWorkspaceUrlId)) {
                const sectionPaths: Record<string, string> = {
                    'my-tasks': '/my-tasks',
                    'team': '/team',
                    'all-tasks': '/all-tasks',
                    'projects': '/projects',
                }
                const sectionPath = sectionPaths[data.loginSection || 'projects'] ?? ''
                // Return the visitor to the task/project link they came for before being
                // sent to /auth, if it points to a workspace they belong to (SCD-237).
                const authNext = sessionStorage.getItem('authNext')
                if (authNext) {
                    sessionStorage.removeItem('authNext')
                    const nextWorkspaceUrlId = authNext.split('/')[1]
                    if (nextWorkspaceUrlId && workspaceUrlIds.includes(nextWorkspaceUrlId)) {
                        logTiming(`[auth] loadUserData → redirecting to remembered ${authNext}`)
                        window.location.href = authNext
                        return 'redirecting'
                    }
                }
                // The user is on a first path segment that isn't one of their workspaces. Decide
                // the outcome by whether the workspace actually EXISTS — the workspaceUrlIds
                // mapping is world-readable, so we can tell "exists but no access" (#3+5) from
                // "doesn't exist" (#7) instead of dumping both into /no-access (SCD-936).
                let workspaceExists = false
                const isWorkspaceCandidate = currentWorkspaceUrlId !== '' &&
                    currentWorkspaceUrlId !== 'auth' && currentWorkspaceUrlId !== 'no-access'
                if (isWorkspaceCandidate) {
                    try {
                        const urlIdDoc: any = yield call(getDoc, doc(db, 'workspaceUrlIds', currentWorkspaceUrlId))
                        workspaceExists = urlIdDoc.exists()
                    } catch { workspaceExists = false }
                }
                const outcome = resolveWorkspaceEntry({
                    slug: currentWorkspaceUrlId,
                    userBelongs: false,
                    workspaceExists,
                })

                // Exists but not a member → standalone no-access screen, preserving the link.
                if (outcome === 'no-access') {
                    logTiming('[auth] loadUserData → no-access screen')
                    window.location.href = `/no-access?next=${encodeURIComponent(sanitizeNext(authNext || currentPath))}`
                    return 'redirecting'
                }
                // Root / auth → existing default-workspace or picker behavior.
                if (outcome === 'default') {
                    const target = resolveDefaultWorkspace(workspaceUrlIds, data.lastWorkspaceUrlId)
                    if (target) {
                        logTiming(`[auth] loadUserData → redirecting to /${target}${sectionPath}`)
                        window.location.href = `/${target}${sectionPath}`
                        return 'redirecting'
                    }
                    if (currentWorkspaceUrlId !== '') {
                        window.location.href = '/'
                        return 'redirecting'
                    }
                    showWorkspacePicker = true
                }
                // 'standalone' (/no-access) and 'not-found' (unknown workspace) DON'T redirect:
                // set auth and stay on the URL. /no-access is rendered by App; an unknown
                // workspace slug renders NotFoundPage via WorkspaceRouteGuard. Mark the
                // standalone case so the loader is dropped without waiting on a workspace load.
                renderStandalone = outcome === 'standalone'
            }

            // Already on a valid workspace URL — set auth and continue
            yield put(setAuth({
                id: user.uid,
                email: user.email || '',
                fullName: data.fullName || '',
                color: data.color || '',
                avatarImage: data.avatarImage || '',
                username: data.username || '',
                timezone: data.timezone || '',
                loginSection: data.loginSection || 'projects',
                lastWorkspaceUrlId: data.lastWorkspaceUrlId ?? null,
                workspaces: userWorkspaces,
                // Already populated above — no need to gate the app on the userWorkspaces
                // listener re-fetching the same workspace docs.
                workspacesLoading: false,
                notificationSettings: DEFAULT_NOTIFICATION_SETTINGS,
            }))

            setPersonProps({ email: user.email || undefined, name: data.fullName || undefined })

            // invite_accepted (best-effort): a user landing on their first load right
            // after signup with a non-owner workspace joined via an invite (onUserCreated
            // converts the invitation into a 'member' membership). Solo signups create
            // their own workspace and become 'owner' (flag cleared in createWorkspaceWorker).
            if (consumeJustSignedUp() && userWorkspaces.some((w) => w.role !== 'owner')) {
                trackEvent(AnalyticsEvent.InviteAccepted)
            }

            if (showWorkspacePicker) {
                yield put(setOpenModal({ type: 'selectWorkspace', canClose: false }))
                logTiming('[auth] loadUserData → select-workspace (picker)')
                return 'select-workspace'
            }

            if (renderStandalone) {
                // On /no-access: auth is set, App renders the screen. Drop the loader directly
                // (no WorkspaceProvider here, so don't wait on a workspace load) and never redirect.
                logTiming('[auth] loadUserData → standalone (/no-access)')
                return 'standalone'
            }

            logTiming('[auth] loadUserData → ready (setAuth dispatched)')
            return 'ready'
        }
    } else {
        yield put(resetAuth())
        return 'no-user'
    }
}

// Watchdog ceiling for each bootstrap phase. A wedged Firestore long-poll channel
// or a locked multi-tab IndexedDB lease can leave a getDoc/onSnapshot pending
// forever (Firestore reads have no client timeout), which would otherwise pin the
// splash up indefinitely (the "eternal loader"). On timeout we drop the splash
// anyway and let the page render from cache / its own empty+loading states, rather
// than hang. persistentLocalCache makes a returning user's snapshot near-instant,
// so this only ever fires on a genuinely stuck connection.
const APP_LOADED_PHASE_TIMEOUT_MS = 8000

// Wait until `predicate` holds, waking only on the specific actions that can flip
// the watched flags (not every dispatched action — `take('*')` spun this loop on
// the dozens of upsert actions fired during the initial Firestore wave). Checks
// the current state first so an already-satisfied condition returns immediately.
function* waitForState(
    actionTypes: string[],
    predicate: (state: RootState) => boolean
): Generator<any, void, any> {
    while (true) {
        const state: RootState = yield select()
        if (predicate(state)) return
        yield take(actionTypes)
    }
}

function* waitForAppLoaded(): Generator<any, void, any> {
    logTiming('[auth] waitForAppLoaded: shell phase start (auth + workspaces)')
    // Drop the splash the moment the SHELL can paint — identity + the workspace list resolved.
    // The sidebar renders entirely from auth.workspaces (name + avatar, served from the
    // IndexedDB cache), so it needs neither the workspace doc nor projects. We deliberately do
    // NOT wait for the workspace doc here (SCD-890 did): waiting kept the full-screen splash up
    // as a second, distinct "workspace loader" before the sidebar. Now the splash covers only
    // the pre-shell window; the workspace doc and each screen's data fill in behind a SINGLE
    // in-content Loader. The heavy deferred listeners still wait for the first projects snapshot
    // (see startDeferredListeners), so the cold Firestore wave timing is unchanged.
    const phase1: { timeout?: true } = yield race({
        done: call(
            waitForState,
            [setAuth.type, setWorkspacesLoading.type],
            (s) => s.auth !== null && !s.auth.workspacesLoading
        ),
        timeout: delay(APP_LOADED_PHASE_TIMEOUT_MS),
    })
    if (phase1.timeout !== undefined) {
        logTiming('[auth] waitForAppLoaded: shell phase TIMEOUT → dropping splash anyway')
        yield put(setGlobalLoading(false))
        return
    }
    logTiming('[auth] waitForAppLoaded: shell ready')

    // The root "/" entry is a special case: there's no shell at "/" (it's a redirect), and the
    // redirect target depends on the projects snapshot (RootRedirect → first project). So for that
    // entry keep the splash until projects load — otherwise the screen would flash blank. Every
    // direct workspace-route visit (/:workspace/...) renders the shell, so it drops on shell-ready
    // and shows its own in-content Loader.
    if (window.location.pathname === '/') {
        logTiming('[auth] waitForAppLoaded: root entry → also wait for projects snapshot')
        yield race({
            done: call(waitForState, [setProjectsLoading.type], (s) => !s.projects.loading),
            timeout: delay(APP_LOADED_PHASE_TIMEOUT_MS),
        })
    }
    yield put(setGlobalLoading(false))
}

// How long to wait for the first onAuthStateChanged before provisionally treating
// the session as logged-out and dropping the splash (see the watchdog below).
const AUTH_WATCHDOG_MS = 2500

// loadUserData: how hard to retry the users/{uid} fetch before giving up, to ride
// out the onUserCreated trigger lag on a brand-new account (SCD-531).
const USER_DOC_MAX_ATTEMPTS = 5
const USER_DOC_RETRY_MS = 600

// One-shot guard (per tab) so a permission-denied first read triggers at most one
// recovery reload — never a reload loop on a genuine denial. Cleared on a
// successful read so a later stuck state can recover again.
const AUTH_RELOAD_FLAG = 'plate_auth_reload'

function* initAuthWorker(): Generator<any, void, any> {
    logTiming('[auth] initAuthWorker start')
    // getRedirectResult is only needed to complete a Google sign-in redirect (and create the
    // new user's Firestore profile). It blocks ~470ms, so only call it when we actually
    // returned from a redirect; otherwise onAuthStateChanged provides the already-signed-in user.
    if (hasPendingGoogleRedirect()) {
        yield call(handleGoogleRedirect)
        logTiming('[auth] handleGoogleRedirect done (returned from redirect)')
    } else {
        logTiming('[auth] skipped getRedirectResult (no pending redirect)')
    }

    const authChannel: ReturnType<typeof createAuthChannel> = yield call(createAuthChannel)
    let workspacesTask: any = null
    let watchdogArmed = true

    while (true) {
        let result: User | 'LOGGED_OUT'
        if (watchdogArmed) {
            watchdogArmed = false
            // Watchdog (SCD-670): the first onAuthStateChanged can stall for many
            // seconds (cold Firestore long-poll handshake / IndexedDB lock / stale
            // token refresh), leaving a logged-out visitor — e.g. someone opening a
            // workspace invite deep link — staring at the splash. If it hasn't
            // resolved within AUTH_WATCHDOG_MS, drop the splash so the sign-in form
            // (or the deep-link → /auth redirect) renders, then keep waiting for the
            // real event and reconcile (a signed-in user re-raises the splash below).
            const raced: { event?: User | 'LOGGED_OUT' } = yield race({
                event: take(authChannel),
                timeout: delay(AUTH_WATCHDOG_MS),
            })
            if (raced.event !== undefined) {
                result = raced.event
            } else {
                logTiming('[auth] watchdog fired → provisional logged-out, dropping splash')
                yield put(setGlobalLoading(false))
                result = yield take(authChannel)
            }
        } else {
            result = yield take(authChannel)
        }

        // Cancel previous workspaces listener if any
        if (workspacesTask) {
            yield cancel(workspacesTask)
            workspacesTask = null
        }

        if (result !== 'LOGGED_OUT') {
            logTiming(`[auth] authChannel: user detected, uid=${result.uid}`)
            // Tie all PostHog events to a stable user id (needed for D1/D7 retention).
            identifyUser(result.uid, { email: result.email || undefined })
            // Reset guest defaults → loading=true so waitForAppLoaded works
            yield put(resetState())
            yield put(setGlobalLoading(true))

            let status: LoadUserResult
            try {
                status = yield call(loadUserData, result)
            } catch (err) {
                // loadUserData retries transient read failures itself; if it still throws,
                // never pin the splash forever — drop the loader so the app renders and a
                // reload can re-run the bootstrap, instead of an eternal loader.
                console.error('[auth] loadUserData failed, dropping loader', err)
                logTiming('[auth] loadUserData threw → setGlobalLoading(false)')
                yield put(setGlobalLoading(false))
                continue
            }

            // Start workspaces listener for logged-in user
            workspacesTask = yield fork(listenUserWorkspacesWorker, result.uid)

            switch (status) {
                case 'no-user':
                case 'no-workspaces':
                case 'needs-verification':
                case 'select-workspace':
                case 'standalone':
                    logTiming(`[auth] initAuthWorker: status=${status} → setGlobalLoading(false)`)
                    yield put(setGlobalLoading(false))
                    break
                case 'ready':
                    yield fork(waitForAppLoaded)
                    break
                // 'redirecting': page will reload, do nothing
            }
        } else {
            logTiming('[auth] authChannel: LOGGED_OUT')
            resetUser()
            yield put(resetState())
            yield put(createInitialState())
            logTiming('[auth] LOGGED_OUT → setGlobalLoading(false)')
            yield put(setGlobalLoading(false))
        }
    }
}

function* signInWorker(action: PayloadAction<{email: string, password: string}>): Generator<any, void, any> {
    const {email, password} = action.payload
    yield put(setSignInError(null))
    yield put(addLoadingKey('sign-in'))
    try {
        yield call(signInWithEmailAndPassword, auth, email, password)
        yield put(setOpenModal(null))
    } catch (error: any) {
        if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
            try {
                yield call(createUserWithEmailAndPassword, auth, email, password)
                // New email account — funnel step 1. Flag so the first authenticated
                // load can tell whether they landed on an invited workspace.
                markJustSignedUp()
                trackEvent(AnalyticsEvent.SignedUp, { method: 'email' })
                yield put(setOpenModal(null))
            } catch (signUpError: any) {
                console.error('Sign in failed:', error.message)
                yield put(setSignInError('Invalid email or password'))
            }
        } else  {
            console.error('Sign in failed:', error.message)
            yield put(setSignInError('Invalid email or password'))
        }
    } finally {
        yield put(removeLoadingKey('sign-in'))
    }
}

function* signInGoogleWorker(): Generator<any, void, any> {
    yield put(addLoadingKey('sign-in-google'))
    try {
        yield call(signInWithGoogle)
        // Page will redirect to Google — loading key stays until page unloads
    } catch (error: any) {
        console.error('Google sign in failed:', error.message)
        yield put(removeLoadingKey('sign-in-google'))
    }
}

function* resetPasswordWorker(action: PayloadAction<string>): Generator<any, void, any> {
    yield put(addLoadingKey('reset-password'))
    yield put(setResetPasswordError(null))
    try {
        yield call(sendPasswordResetEmail, auth, action.payload)
    } catch (error: any) {
        yield put(setResetPasswordError(error.message))
    } finally {
        yield put(removeLoadingKey('reset-password'))
    }
}

function* verifyEmailWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const code = action.payload
    yield put(addLoadingKey('verify-email'))
    try {
        const verifyEmailFn = httpsCallable(functions, 'verifyUserEmail')
        yield call(() => verifyEmailFn({ code }))
        window.location.reload()
    } catch (error: any) {
        yield put(setSignInError(
            error.code === 'functions/invalid-argument'
                ? 'Invalid verification code'
                : 'Something went wrong. Please try again.'
        ))
    } finally {
        yield put(removeLoadingKey('verify-email'))
    }
}

function* resendVerificationWorker(): Generator<any, void, any> {
    try {
        const sendVerificationCodeFn = httpsCallable(functions, 'sendVerificationCode')
        yield call(() => sendVerificationCodeFn({}))
    } catch (error: any) {
        console.error('resendVerification error:', error)
        yield put(setSignInError('Failed to resend code. Please try again.'))
    }
}

function* logoutWorker(): Generator<any, void, any> {
    // Close any open modal first. Logout is now a client-side nav (not a full reload), so a modal
    // left open — e.g. the email-verification modal whose ✕ dispatches logout — would otherwise
    // persist over the marketing landing instead of closing (SCD-898).
    yield put(setOpenModal(null))
    try {
        yield call(signOut, auth)
    } catch (error: any) {
        console.error('Logout failed:', error.message)
        return
    }
    // Client-side navigation to the marketing landing — NOT a full page reload. The reload
    // (window.location) made logout feel slow: it re-downloads the bundle, re-inits Firebase
    // and IndexedDB, and waits on onAuthStateChanged before the splash drops. By now signOut's
    // reactive LOGGED_OUT branch (initAuthWorker) has already reset the store to guest defaults,
    // and WorkspaceProvider's auth-change effect (still on the workspace URL here) tore down the
    // Firestore listeners via takeLatest — so an in-place replace to '/' is both safe and fast.
    // We go to '/' (not /auth) so the deep-link guard doesn't bounce the now-logged-out
    // workspace URL to the login form.
    history.replace('/')
}

// Persist the last opened workspace on the user doc so it follows them across
// devices and survives cache clears. Skipped when unchanged to avoid redundant
// writes on every workspace page load.
function* updateLastWorkspaceWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const workspaceUrlId = action.payload
    if (!workspaceUrlId) return
    const auth: AuthState | null = yield select((state: RootState) => state.auth)
    if (!auth?.id || auth.lastWorkspaceUrlId === workspaceUrlId) return
    yield put(setLastWorkspaceUrlId(workspaceUrlId))
    try {
        yield call(() => updateDoc(doc(db, 'users', auth.id), {lastWorkspaceUrlId: workspaceUrlId}))
    } catch (err) {
        console.error('Error updating lastWorkspaceUrlId:', err)
    }
}

function* updateUserAvatarWorker(action: PayloadAction<string | null>): Generator<any, void, any> {
    const userId: string = yield select((state: RootState) => state.auth?.id)
    if (!userId) return
    try {
        yield put(setAvatarUrl(action.payload))
        yield call(() => updateDoc(doc(db, 'users', userId), {avatarImage: action.payload}))
        showToast('Avatar updated')
    } catch (err) {
        console.error('Error updating user avatar:', err)
    }
}

function* updateUsernameWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const userId: string = yield select((state: RootState) => state.auth?.id)
    if (!userId) return
    try {
        yield put(setUsername(action.payload))
        yield call(() => updateDoc(doc(db, 'users', userId), {username: action.payload}))
        showToast('Username updated')
    } catch (err) {
        console.error('Error updating username:', err)
    }
}

function* updateEmailWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const userId: string = yield select((state: RootState) => state.auth?.id)
    if (!userId) return
    try {
        yield put(setEmail(action.payload))
        yield call(() => updateDoc(doc(db, 'users', userId), {email: action.payload}))
        showToast('Email updated')
    } catch (err) {
        console.error('Error updating email:', err)
    }
}

function* sendEmailChangeWorker(action: PayloadAction<{ newEmail: string; onSuccess?: () => void; onError?: (msg: string) => void }>): Generator<any, void, any> {
    const {newEmail, onSuccess, onError} = action.payload
    try {
        const sendEmailChangeCodeFn = httpsCallable(functions, 'sendEmailChangeCode')
        yield call(() => sendEmailChangeCodeFn({newEmail}))
        if (onSuccess) onSuccess()
    } catch (error: any) {
        console.error('sendEmailChange error:', error)
        if (onError) onError('Failed to send verification code. Please try again.')
    }
}

function* verifyEmailChangeWorker(action: PayloadAction<{ code: string; onSuccess?: () => void; onError?: (msg: string) => void }>): Generator<any, void, any> {
    const {code, onSuccess, onError} = action.payload
    try {
        const verifyEmailChangeFn = httpsCallable(functions, 'verifyEmailChange')
        const result: any = yield call(() => verifyEmailChangeFn({code}))
        if (result.data?.email) {
            yield put(setEmail(result.data.email))
        }
        if (onSuccess) onSuccess()
    } catch (error: any) {
        console.error('verifyEmailChange error:', error)
        const msg = error.code === 'functions/invalid-argument'
            ? 'Invalid verification code'
            : 'Failed to verify code. Please try again.'
        if (onError) onError(msg)
    }
}

function buildDeleteAccountText(workspaces: {id: string; name: string}[]): string {
    if (workspaces.length === 0) {
        return 'Are you sure you want to delete your account? This action is permanent and cannot be undone.'
    }
    const workspaceList = workspaces.map(w => `- ${w.name}`).join('\n')
    return `You are the only owner of the following workspaces:\n\n${workspaceList}\n\nThese workspaces and all their data will be permanently deleted along with your account. This action cannot be undone.`
}

function* initiateDeleteAccountWorker(): Generator<any, void, any> {
    // The sole-owner check is informational only — deleteAccount does the real cascade.
    // If the check fails, still offer the dialog (with the strongest wording) instead of
    // dead-ending the delete flow with a silent console error.
    let text = 'Are you sure you want to delete your account? Any workspace you are the only owner of will be permanently deleted along with all of its data. This action cannot be undone.'
    try {
        const checkFn = httpsCallable(functions, 'checkSoleOwnerWorkspaces')
        const result: any = yield call(() => checkFn({}))
        const workspaces: {id: string; name: string}[] = result.data.workspaces
        text = buildDeleteAccountText(workspaces)
    } catch (error: any) {
        console.error('initiateDeleteAccount error:', error)
    }

    yield put(openConfirmDialog({
        title: 'Delete account',
        text,
        confirmButtonTitle: 'Delete',
        confirmButtonAction: () => store.dispatch(deleteAccountSaga({})),
    }))
}

function* deleteAccountWorker(action: PayloadAction<{ onError?: (msg: string) => void }>): Generator<any, void, any> {
    const {onError} = action.payload
    try {
        const deleteAccountFn = httpsCallable(functions, 'deleteAccount')
        yield call(() => deleteAccountFn({}))
        yield call(signOut, auth)
        window.location.href = '/'
    } catch (error: any) {
        console.error('deleteAccount error:', error)
        if (onError) onError('Failed to delete account. Please try again.')
    }
}

function* updateLoginSectionWorker(action: PayloadAction<LoginSection>): Generator<any, void, any> {
    const userId: string = yield select((state: RootState) => state.auth?.id)
    if (!userId) return
    try {
        yield put(setLoginSection(action.payload))
        yield call(() => updateDoc(doc(db, 'users', userId), {loginSection: action.payload}))
    } catch (err) {
        console.error('Error updating loginSection:', err)
    }
}

function* updateFullNameWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const userId: string = yield select((state: RootState) => state.auth?.id)
    if (!userId) return
    try {
        yield put(setFullName(action.payload))
        yield call(() => updateDoc(doc(db, 'users', userId), {fullName: action.payload}))
        showToast('Name updated')
    } catch (err) {
        console.error('Error updating fullName:', err)
    }
}

function* loadNotificationSettingsWorker(): Generator<any, void, any> {
    const userId: string = yield select((state: RootState) => state.auth?.id)
    if (!userId) return
    try {
        const snap: any = yield call(() => getDoc(doc(db, 'user_notification_settings', userId)))
        if (snap.exists()) {
            const data = snap.data() as Partial<NotificationSettings>
            yield put(setNotificationSettings({
                emailEnabled: data.emailEnabled ?? true,
                assignments: data.assignments ?? true,
                comments: data.comments ?? true,
                mentions: data.mentions ?? true,
                weeklyDigest: data.weeklyDigest ?? true,
                productUpdates: data.productUpdates ?? true,
            }))
        }
    } catch (err) {
        console.error('Error loading notification settings:', err)
    }
}

function* updateNotificationSettingsWorker(action: PayloadAction<Partial<NotificationSettings>>): Generator<any, void, any> {
    const userId: string = yield select((state: RootState) => state.auth?.id)
    if (!userId) return
    const current: NotificationSettings = yield select((state: RootState) => state.auth?.notificationSettings ?? DEFAULT_NOTIFICATION_SETTINGS)
    const updated: NotificationSettings = {...current, ...action.payload}
    yield put(setNotificationSettings(updated))
    try {
        yield call(() => setDoc(doc(db, 'user_notification_settings', userId), updated, {merge: true}))
    } catch (err) {
        console.error('Error saving notification settings:', err)
    }
}

export function* watchAuthSaga() {
    yield takeLatest(initAuthSaga.type, initAuthWorker)
    yield takeLatest(signInSaga.type, signInWorker)
    yield takeLatest(signInGoogleSaga.type, signInGoogleWorker)
    yield takeLatest(resetPasswordSaga.type, resetPasswordWorker)
    yield takeLatest(logoutSaga.type, logoutWorker)
    yield takeLatest(verifyEmailSaga.type, verifyEmailWorker)
    yield takeLatest(resendVerificationSaga.type, resendVerificationWorker)
    yield takeLatest(updateLoginSectionSaga.type, updateLoginSectionWorker)
    yield takeLatest(updateLastWorkspaceSaga.type, updateLastWorkspaceWorker)
    yield takeLatest(updateFullNameSaga.type, updateFullNameWorker)
    yield takeLatest(updateUserAvatarSaga.type, updateUserAvatarWorker)
    yield takeLatest(updateUsernameSaga.type, updateUsernameWorker)
    yield takeLatest(updateEmailSaga.type, updateEmailWorker)
    yield takeLatest(sendEmailChangeSaga.type, sendEmailChangeWorker)
    yield takeLatest(verifyEmailChangeSaga.type, verifyEmailChangeWorker)
    yield takeLatest(initiateDeleteAccountSaga.type, initiateDeleteAccountWorker)
    yield takeLatest(deleteAccountSaga.type, deleteAccountWorker)
    yield takeLatest(loadNotificationSettingsSaga.type, loadNotificationSettingsWorker)
    yield takeLatest(updateNotificationSettingsSaga.type, updateNotificationSettingsWorker)
}
